import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyableInputField } from '@/core/CopyableInputField.molecule';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { sqWorksheetStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { onEnterKeypress } from '@/core/onEnterKeypress.util';
import { formatTime as formatDateTime } from '@/datetime/dateTime.utilities';
import { getPreferredTimeFormat } from '@/datetime/dateTimeFormatting.utilities';
import { useDateTimeFormatPreferences } from '@/datetime/useDateTimeFormatPreferences.hook';
import { successToast } from '@/utilities/toast.utilities';
import { sqAccessKeysApi } from '@seeqdev/api/endpoint/AccessKeysApi';
import { Button, InputGroup, Modal, TextField } from '@seeqdev/qomponents';
import type { AccessKeyOutputV1 } from '@seeqdev/api/model/AccessKeyOutputV1';

export interface AccessKeyModalProps {
  creationEnabled: boolean;
  onClose: () => void;
}

export const AccessKeyModal = ({ creationEnabled, onClose }: AccessKeyModalProps) => {
  const { t } = useTranslation();
  const [newCommonName, setNewCommonName] = useState('');
  const [keyPassword, setKeyPassword] = useState('');
  const [keyName, setKeyName] = useState('');
  const [showCreatedKey, setShowCreatedKey] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [disableCreateButton, setDisableCreateButton] = useState(true);
  const [keyDeletionCandidate, setKeyDeletionCandidate] = useState<{
    name: string;
    description?: string;
  }>({
    name: '',
    description: '',
  });
  const [accessKeys, setAccessKeys] = useState<AccessKeyOutputV1[]>([]);

  const { use24HourFormat } = useDateTimeFormatPreferences();
  const timezone = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.timezone);

  useEffect(() => {
    refreshKeys();
  }, []);

  /**
   * Fetches all the Access Keys from Seeq and refreshes the list in the modal.
   */
  const refreshKeys = () => {
    sqAccessKeysApi
      .getKeys({ offset: 0, limit: 1000, getAll: false })
      .then(({ data }) => setAccessKeys(data.keys as AccessKeyOutputV1[]));
  };

  /**
   * Dispatches a request to create a new Access Key with the given common name.
   *
   * @param label - the common name to be associated with the new token.
   */
  const createKey = (label: string) => {
    setNewCommonName('');
    return sqAccessKeysApi.createKey({ name: label.trim() }).then(({ data: key }) => {
      setKeyPassword(key.password as string);
      setKeyName(key.name);
      setShowCreatedKey(true);
      refreshKeys();
    });
  };

  /**
   * Deletes the current keyDeletionCandidate and notifies the user.
   */
  const deleteKey = () => {
    return sqAccessKeysApi
      .deleteKey({ keyName: keyDeletionCandidate.name })
      .then(() => refreshKeys())
      .then(() => {
        setShowDeleteWarning(false);
        setKeyDeletionCandidate({ name: '', description: '' });
        successToast({ messageKey: 'ACCESS_KEY.DELETE_SUCCESS' });
      });
  };

  /**
   * Shows the delete warning for the given token and gives the user the option to confirm deletion.
   *
   * @param token - The token to warn about deleting.
   */
  const deleteWarning = (token: AccessKeyOutputV1) => {
    setShowDeleteWarning(true);
    setKeyDeletionCandidate(token);
  };

  /**
   * Formats timestamps for the token modal.
   *
   * @param dateTime - the timestamp to format.
   * @param summary - generates a short date if true, or a long date if false.
   */
  const formatTime = (dateTime: string, summary: boolean) => {
    if (!dateTime) return '';
    if (summary) return formatDateTime(dateTime, timezone, 'MMM D, YYYY');
    const longFormat = getPreferredTimeFormat(use24HourFormat, 'MMM D, YYYY h:mm A');
    return formatDateTime(dateTime, timezone, longFormat);
  };

  // CSS classes for minimum widths of table columns.
  const LABEL_MIN_WIDTH = 'width-130';
  const CREATED_AT_MIN_WIDTH = 'width-90';
  const LAST_USED_MIN_WIDTH = 'width-90';
  const KEY_NAME_MIN_WIDTH = 'width-170';
  const DELETE_BUTTON_MIN_WIDTH = 'width-40';

  /**
   * Builds the header row for the token table.
   */
  const buildKeyHeader = () => {
    return (
      <div className="flexColumnContainer fakeTableHeader">
        <div className={LABEL_MIN_WIDTH}>{t('ACCESS_KEY.NAME')}</div>
        <div className={CREATED_AT_MIN_WIDTH}>{t('ACCESS_KEY.CREATED_AT')}</div>
        <div className={LAST_USED_MIN_WIDTH}>{t('ACCESS_KEY.LAST_USED')}</div>
        <div className={KEY_NAME_MIN_WIDTH}>{t('ACCESS_KEY.KEY_NAME')}</div>
        <div className={DELETE_BUTTON_MIN_WIDTH} />
      </div>
    );
  };

  /**
   * Builds the body rows for the token table.
   */
  const buildKeyBodyRows = () => {
    return _.map(accessKeys, (item) => {
      return (
        <div
          className="fakeTableRow fakeTableStriped py5 tw:flex tw:min-h-sq-40! tw:items-center"
          data-testid="key-row"
          key={item.name}>
          <HoverTooltip text={item.description}>
            <div className={LABEL_MIN_WIDTH}>{item.description}</div>
          </HoverTooltip>
          <HoverTooltip text={formatTime(item.createdAt, false)}>
            <div className={CREATED_AT_MIN_WIDTH}>{formatTime(item.createdAt, true)}</div>
          </HoverTooltip>
          <HoverTooltip text={item.lastUsed ? formatTime(item.lastUsed as string, false) : undefined}>
            <div className={LAST_USED_MIN_WIDTH}>{item.lastUsed ? formatTime(item.lastUsed as string, true) : ''}</div>
          </HoverTooltip>
          <div className={KEY_NAME_MIN_WIDTH}>
            <CopyableInputField
              value={item.name}
              notifyMessage="ACCESS_KEY.COPY_SUCCESS_NAME"
              fieldTooltip="ACCESS_KEY.KEY_NAME"
              readOnly={true}
              buttonTooltip="ACCESS_KEY.COPY_TO_CLIPBOARD"
              testId={`${item.name}row`}
            />
          </div>
          <Button
            testId="delete-button"
            variant="danger"
            icon="fa-trash-can"
            iconStyle="white"
            tooltip={t('ACCESS_KEY.DELETE')}
            onClick={() => deleteWarning(item)}
            extraClassNames="muted ml5"
          />
        </div>
      );
    });
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      testId="accessKeyModal"
      title={t('ACCESS_KEY.MODAL_TITLE')}
      hideSubmitButton={true}
      cancelButtonLabel={t('CLOSE')}>
      <div>
        <div className="tw:mb-sq-10">
          <InputGroup
            testId="create-key-field"
            autoComplete="off"
            type="text"
            name="commonName"
            extraClassNames="width-maximum"
            value={newCommonName}
            onChange={(e) => {
              setNewCommonName(e.target.value);
              setDisableCreateButton(e.target.value.trim().length === 0);
            }}
            placeholder={t('ACCESS_KEY.NAME')}
            required={true}
            onKeyUp={onEnterKeypress(createKey)}
            disabled={!creationEnabled}
            append={[
              {
                variant: 'button',
                buttonProps: {
                  label: t('ACCESS_KEY.CREATE_KEY'),
                  testId: 'create-key-button',
                  onClick: () => createKey(newCommonName),
                  disabled: !creationEnabled || disableCreateButton,
                  variant: 'theme',
                },
              },
            ]}
          />
        </div>
        {!creationEnabled && (
          <div className="small tw:mt-sq-5 tw:mb-sq-10 tw:italic">{t('ACCESS_KEY.CREATION_DISABLED')}</div>
        )}
        <div data-testid="created-key-info" hidden={!showCreatedKey} className="alert sqGrayBox flexRowContainer">
          <div className="tw:mb-sq-10">{t('ACCESS_KEY.NEW_KEY_DETAILS')}</div>
          <div className="tw:mb-sq-5 tw:flex tw:justify-center">
            <label className="tw:flex tw:min-w-sq-75! tw:items-center">{t('ACCESS_KEY.KEY_NAME')}</label>
            <div className="">
              <CopyableInputField
                testId="new-key-name"
                readOnly={true}
                fieldTooltip="ACCESS_KEY.KEY_NAME"
                buttonTooltip="ACCESS_KEY.COPY_TO_CLIPBOARD"
                notifyMessage="ACCESS_KEY.COPY_SUCCESS_NAME"
                value={keyName}
                extraClassNames="min-width-160"
              />
            </div>
          </div>
          <div className="tw:mb-sq-5 tw:flex tw:justify-center">
            <label className="tw:flex tw:min-w-sq-75! tw:items-center">{t('ACCESS_KEY.KEY_PASSWORD')}</label>
            <div className="">
              <CopyableInputField
                testId="new-key-password"
                readOnly={true}
                fieldTooltip="ACCESS_KEY.KEY_PASSWORD"
                value={keyPassword}
                buttonTooltip="ACCESS_KEY.COPY_TO_CLIPBOARD"
                notifyMessage="ACCESS_KEY.COPY_SUCCESS_PASSWORD"
                extraClassNames="min-width-160"
              />
            </div>
          </div>
        </div>
        <div
          data-testid="delete-banner"
          hidden={!showDeleteWarning}
          className="alert roundedBorder lightGreyBorder flexRowContainer">
          <div className="tw:mb-sq-10">{t('ACCESS_KEY.DELETE_WARNING')}</div>
          <div className="tw:mb-sq-5 tw:flex tw:justify-center">
            <label className="tw:flex tw:min-w-sq-75! tw:items-center">{t('ACCESS_KEY.NAME')}</label>
            <TextField
              testId="delete-key-common-name"
              type="text"
              disabled={true}
              value={keyDeletionCandidate.description}
              extraClassNames="min-width-200"
            />
          </div>
          <div className="tw:mb-sq-5 tw:flex tw:justify-center">
            <label className="tw:flex tw:min-w-sq-75! tw:items-center">{t('ACCESS_KEY.KEY_NAME')}</label>
            <TextField
              testId="delete-key-name"
              type="text"
              disabled={true}
              value={keyDeletionCandidate.name}
              extraClassNames="min-width-200"
            />
          </div>
          <div className="tw:mt-sq-5 tw:flex tw:justify-end">
            <Button onClick={() => setShowDeleteWarning(false)} label={t('CANCEL')} testId="cancelDeleteButton" />
            <Button
              variant="danger"
              onClick={deleteKey}
              label={t('DELETE')}
              extraClassNames="ml5"
              testId="confirmDeleteButton"
            />
          </div>
        </div>
        <div hidden={accessKeys.length === 0} className="tw:flex tw:justify-center">
          <div className="flexRowContainer fakeTableWrapper tw:items-start">
            {buildKeyHeader()}
            {buildKeyBodyRows()}
          </div>
        </div>
      </div>
    </Modal>
  );
};
